import { AlertCircle, Check, Copy, RefreshCw, Server, User as UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CopyableField } from '@/components/custom-ui/CopyableField'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/utils/cn'

export interface SftpSectionProps {
  username: string
  /** Revealed one-time password after a reset, else null. */
  password: string | null
  resetting: boolean
  onReset: () => void
  address?: string
}

export function SftpSection({
  username,
  password,
  resetting,
  onReset,
  address = 'localhost:2022',
}: SftpSectionProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const handleCopyPassword = async () => {
    if (!password) return
    await navigator.clipboard.writeText(password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.sftp.title')}</CardTitle>
        <CardDescription>{t('settings.sftp.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <CopyableField label={t('settings.sftp.address')} value={address} icon={Server} mono />
        <CopyableField label={t('settings.sftp.username')} value={username} icon={UserIcon} mono />

        <Separator />

        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs font-normal">
            {t('settings.sftp.password')}
          </Label>
          <InputGroup>
            <InputGroupInput
              readOnly
              type={password ? 'text' : 'password'}
              value={password || '••••••••••••'}
              className="font-mono"
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                variant="ghost"
                className="max-md:relative max-md:after:absolute max-md:after:-inset-1.5"
                onClick={password ? handleCopyPassword : undefined}
                disabled={!password}
              >
                {copied ? <Check className="text-success" /> : <Copy />}
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
          <Button
            size="sm"
            variant="outline"
            className="max-md:h-9"
            onClick={onReset}
            disabled={resetting}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', resetting && 'animate-spin')} />
            {resetting ? t('settings.sftp.resetting') : t('settings.sftp.resetPassword')}
          </Button>
          {password && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 pt-0.5">
              <AlertCircle className="h-3 w-3" />
              {t('settings.sftp.passwordWarning')}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
